
import { useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { BUCKET_NAME, DEFAULT_FILE_SIZE_LIMIT } from './constants';

// Cache para armazenar resultados de verificação de buckets
const bucketCache = new Map<string, { exists: boolean; timestamp: number }>();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos

export function useBucketManagement() {
  // Verificar se um bucket existe (com cache)
  const checkBucketExists = useCallback(async (bucketName: string) => {
    try {
      // Verificar cache primeiro
      const cached = bucketCache.get(bucketName);
      if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
        console.log(`Bucket ${bucketName} existe? ${cached.exists} (do cache)`);
        return { exists: cached.exists, error: null, fromCache: true };
      }
      
      console.log(`Verificando se o bucket ${bucketName} existe...`);
      
      // Primeiro, tentar listar arquivos diretamente no bucket como teste rápido
      try {
        const { data, error } = await supabase
          .storage
          .from(bucketName)
          .list();
          
        if (!error) {
          console.log(`Bucket ${bucketName} existe (verificado por acesso direto)!`);
          bucketCache.set(bucketName, { exists: true, timestamp: Date.now() });
          return { exists: true, error: null, files: data, fromCache: false };
        }
      } catch (accessError) {
        console.log('Erro no teste rápido de acesso ao bucket, verificando via listagem de buckets...');
      }
      
      // Listar todos os buckets disponíveis
      const { data: buckets, error: bucketError } = await supabase
        .storage
        .listBuckets();
        
      if (bucketError) {
        // Se o erro for "Service not enabled", provavelmente significa que o storage está ativado
        // mas o usuário não tem acesso para listar buckets, o que pode significar que o bucket existe
        if (bucketError.message?.includes('Service not enabled')) {
          console.log('Não foi possível listar buckets, verificando novamente por acesso direto');
          
          // Tentar novamente o acesso direto para confirmar
          try {
            const { error: retryError } = await supabase
              .storage
              .from(bucketName)
              .list();
              
            if (!retryError) {
              console.log(`Bucket ${bucketName} confirmado por acesso direto!`);
              bucketCache.set(bucketName, { exists: true, timestamp: Date.now() });
              return { exists: true, error: null, fromCache: false };
            }
          } catch (retryAccessError) {
            console.log('Falha na verificação por acesso direto');
          }
          
          // Se as verificações falharem e não pudermos confirmar, assumimos que o bucket não existe
          console.log(`Não foi possível confirmar se o bucket ${bucketName} existe. Assumindo que não existe.`);
          bucketCache.set(bucketName, { exists: false, timestamp: Date.now() });
          return { exists: false, error: bucketError, fromCache: false };
        }
        
        console.error('Erro ao listar buckets:', bucketError);
        bucketCache.set(bucketName, { exists: false, timestamp: Date.now() });
        return { exists: false, error: bucketError, fromCache: false };
      }
      
      console.log('Buckets disponíveis:', buckets?.map(b => b.name) || []);
      
      const bucketExists = buckets?.some(b => b.name === bucketName) || false;
      console.log(`Bucket ${bucketName} existe? ${bucketExists}`);
      
      // Armazenar no cache
      bucketCache.set(bucketName, { exists: bucketExists, timestamp: Date.now() });
      
      return { exists: bucketExists, error: null, buckets, fromCache: false };
    } catch (error) {
      console.error('Erro ao verificar bucket:', error);
      bucketCache.set(bucketName, { exists: false, timestamp: Date.now() });
      return { exists: false, error, fromCache: false };
    }
  }, []);
  
  // Criar bucket se não existir (com otimização)
  const createBucketIfNotExists = useCallback(async (bucketName: string) => {
    try {
      console.log(`Iniciando verificação/criação do bucket ${bucketName}...`);
      const { exists, error: checkError, buckets, fromCache } = await checkBucketExists(bucketName);
      
      if (checkError) {
        console.error('Erro ao verificar se o bucket existe:', checkError);
        // Se veio do cache, tentar verificar novamente sem cache
        if (fromCache) {
          console.log('Tentando verificar novamente sem cache...');
          bucketCache.delete(bucketName);
          const freshResult = await checkBucketExists(bucketName);
          if (freshResult.exists) {
            return { success: true, message: 'Bucket já existe', error: null };
          }
        }
        // Mesmo com erro, vamos tentar criar o bucket
        console.log('Tentando criar o bucket mesmo com erro na verificação...');
      }
      
      if (!exists) {
        console.log(`Bucket ${bucketName} não encontrado. Tentando criar...`);
        
        // Tentativa de criação do bucket
        try {
          const { data, error } = await supabase
            .storage
            .createBucket(bucketName, {
              public: true,
              fileSizeLimit: DEFAULT_FILE_SIZE_LIMIT,
            });
            
          if (error) {
            // Se o erro for "The resource already exists", consideramos como sucesso
            if (error.message?.includes("already exists")) {
              console.log(`Bucket ${bucketName} já existe (confirmado por erro de criação)`);
              bucketCache.set(bucketName, { exists: true, timestamp: Date.now() });
              return { success: true, message: 'Bucket já existe', error: null };
            }
            
            console.error(`Erro ao criar bucket ${bucketName}:`, error);
            bucketCache.set(bucketName, { exists: false, timestamp: Date.now() });
            return { success: false, error, buckets };
          }
          
          console.log(`Bucket ${bucketName} criado com sucesso:`, data);
          
          // Atualizar cache
          bucketCache.set(bucketName, { exists: true, timestamp: Date.now() });
          
          // Verificar novamente para confirmar a criação (sem usar cache)
          bucketCache.delete(bucketName);
          const verifyResult = await checkBucketExists(bucketName);
          
          if (verifyResult.exists) {
            console.log(`Criação do bucket ${bucketName} confirmada!`);
            return { success: true, data, error: null };
          } else {
            console.warn(`Bucket ${bucketName} criado mas não encontrado na verificação!`);
            return { success: true, warning: 'Bucket criado mas não confirmado', data, error: null };
          }
        } catch (createError: any) {
          // Se o erro for "The resource already exists", consideramos como sucesso
          if (createError?.message?.includes("already exists")) {
            console.log(`Bucket ${bucketName} já existe (confirmado por erro de criação)`);
            bucketCache.set(bucketName, { exists: true, timestamp: Date.now() });
            return { success: true, message: 'Bucket já existe', error: null };
          }
          
          console.error(`Erro ao criar bucket ${bucketName}:`, createError);
          bucketCache.set(bucketName, { exists: false, timestamp: Date.now() });
          return { success: false, error: createError, buckets };
        }
      }
      
      return { success: true, message: 'Bucket já existe', error: null, buckets };
    } catch (error) {
      console.error(`Erro ao criar bucket ${bucketName}:`, error);
      bucketCache.set(bucketName, { exists: false, timestamp: Date.now() });
      return { success: false, error };
    }
  }, [checkBucketExists]);

  // Criar diretórios base no bucket
  const createBaseFolders = useCallback(async (bucketName: string) => {
    try {
      console.log(`Criando pastas base no bucket ${bucketName}...`);

      // Check authentication status first
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        console.error('Usuário não autenticado para criar pastas');
        return { success: false, error: new Error('User not authenticated') };
      }

      const folders = ['system', 'custom'];
      let results = [];

      for (const folder of folders) {
        try {
          // Criar um arquivo marcador para a pasta com o contexto do usuário autenticado
          const markerContent = `Marker file for ${folder} folder created by ${session.user.id}`;
          const markerFile = new Blob([markerContent], { type: 'text/plain' });

          console.log(`Tentando criar marcador para pasta ${folder} com usuário ${session.user.id}`);

          const { data, error } = await supabase
            .storage
            .from(bucketName)
            .upload(`${folder}/.marker`, markerFile, {
              upsert: true,
              cacheControl: '3600'
            });

          if (error) {
            console.error(`Erro ao criar pasta ${folder}:`, error);

            // Detailed error logging for debugging
            if (error.message?.includes('new row violates row-level security policy')) {
              console.error('ERRO DE RLS: Verifique se as políticas de storage estão configuradas corretamente');
              console.error('Detalhes do erro:', JSON.stringify(error, null, 2));
            }

            results.push({ folder, success: false, error });
          } else {
            console.log(`Pasta ${folder} criada/verificada com sucesso`, data);
            results.push({ folder, success: true, data });
          }
        } catch (folderError) {
          console.error(`Erro ao criar pasta ${folder}:`, folderError);
          results.push({ folder, success: false, error: folderError });
        }
      }

      return { success: results.some(r => r.success), results };
    } catch (error) {
      console.error('Erro ao criar pastas base:', error);
      return { success: false, error };
    }
  }, []);
  
  // Verificar e inicializar bucket com pasta
  const initializeBucket = useCallback(async (bucketName: string) => {
    try {
      console.log(`Inicializando bucket ${bucketName}...`);
      
      // 1. Verificar/criar bucket
      const bucketResult = await createBucketIfNotExists(bucketName);
      
      if (!bucketResult.success) {
        console.error('Falha ao criar bucket:', bucketResult.error);
        return {
          success: false,
          bucketResult,
          error: bucketResult.error
        };
      }
      
      // 2. Criar pastas base
      const foldersResult = await createBaseFolders(bucketName);
      
      return {
        success: bucketResult.success && foldersResult.success,
        bucketResult,
        foldersResult,
        error: foldersResult.success ? null : foldersResult.error
      };
    } catch (error) {
      console.error('Erro ao inicializar bucket:', error);
      return { 
        success: false, 
        error 
      };
    }
  }, [createBucketIfNotExists, createBaseFolders]);

  // Limpar cache (útil para debugging ou forçar re-verificação)
  const clearBucketCache = useCallback((bucketName?: string) => {
    if (bucketName) {
      bucketCache.delete(bucketName);
      console.log(`Cache do bucket ${bucketName} limpo`);
    } else {
      bucketCache.clear();
      console.log('Cache de todos os buckets limpo');
    }
  }, []);

  return {
    checkBucketExists,
    createBucketIfNotExists,
    createBaseFolders,
    initializeBucket,
    clearBucketCache
  };
}
